import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Github, Lock, Radio } from "lucide-react";
import { projects, legacyProjects, identity } from "@/data/portfolio";
import { projectSlugs } from "@/data/caseStudies";
import { useGithubRepos } from "@/hooks/useGithubRepos";
import { Section } from "@/components/Section";

const LinkChip = ({ link, testid }) => (
  <a
    href={link.url}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testid}
    className="group/link inline-flex items-center gap-1 border border-white/15 px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider text-zinc-400 transition-colors duration-300 hover:border-cyan-400 hover:text-cyan-400"
  >
    {link.label}
    <ArrowUpRight className="h-3 w-3 transition-transform duration-300 group-hover/link:-translate-y-0.5 group-hover/link:translate-x-0.5" />
  </a>
);

const ProjectCard = ({ p, i, wide }) => {
  const slug = projectSlugs[p.name];
  const slugId = p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  return (
    <motion.article
      data-testid={`project-card-${slugId}`}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.6, delay: i * 0.06 }}
      className={`group relative flex flex-col border border-white/10 bg-[#0A0A0A] p-8 transition-colors duration-500 hover:border-cyan-400/50 md:p-10 ${
        wide ? "lg:col-span-2" : ""
      }`}
    >
      {p.image && (
        <div className="relative mb-8 overflow-hidden border border-white/10">
          <img
            src={p.image}
            alt={`${p.name} interface`}
            loading="lazy"
            className="h-48 w-full object-cover opacity-70 grayscale transition-all duration-700 group-hover:scale-[1.03] group-hover:opacity-100 group-hover:grayscale-0 md:h-56"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
        </div>
      )}
      <div className="mb-3 flex items-center justify-between gap-4">
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan-400">{p.tagline}</span>
        {p.status && (
          <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-zinc-500">
            <Lock className="h-3 w-3" /> {p.status}
          </span>
        )}
      </div>
      <h3 className="font-display text-2xl font-black uppercase tracking-tight text-white md:text-3xl">
        {p.name}
      </h3>
      <p className="mt-4 flex-1 text-sm leading-relaxed text-zinc-400">{p.description}</p>
      <div className="mt-6 flex flex-wrap gap-1.5">
        {p.tags.map((t) => (
          <span key={t} className="bg-white/5 px-2 py-1 font-mono text-[10px] text-zinc-500">
            {t}
          </span>
        ))}
      </div>
      {(p.links.length > 0 || slug) && (
        <div className="mt-6 flex flex-wrap items-center gap-2 border-t border-white/10 pt-5">
          {slug && (
            <Link
              to={`/projects/${slug}`}
              data-testid={`case-study-link-${slugId}`}
              className="group/cs inline-flex items-center gap-2 bg-cyan-400 px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-wider text-black transition-colors duration-300 hover:bg-white"
            >
              Case Study
              <ArrowRight className="h-3 w-3 transition-transform duration-300 group-hover/cs:translate-x-0.5" />
            </Link>
          )}
          {p.links.map((l) => (
            <LinkChip key={l.url} link={l} testid={`project-link-${slugId}-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} />
          ))}
        </div>
      )}
    </motion.article>
  );
};

export const Projects = () => {
  const { repos, live } = useGithubRepos();

  return (
    <Section id="projects" index="02" title="Projects">
      <div className="grid gap-8 lg:grid-cols-2">
        {projects.map((p, i) => (
          <ProjectCard key={p.name} p={p} i={i} wide={p.featured} />
        ))}
      </div>

      <div className="mt-20">
        <h3 className="mb-8 font-mono text-xs uppercase tracking-[0.3em] text-zinc-500">
          {"//"} Earlier venture & product work
        </h3>
        <div data-testid="legacy-projects" className="grid gap-px border border-white/10 bg-white/10 md:grid-cols-2 lg:grid-cols-4">
          {legacyProjects.map((lp) => (
            <div key={lp.name} className="group bg-[#050505] p-6 transition-colors duration-500 hover:bg-[#0A0A0A]">
              <h4 className="font-display text-lg font-bold uppercase text-white group-hover:text-cyan-400">{lp.name}</h4>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-wider text-cyan-400/70">{lp.context}</p>
              <p className="mt-3 text-sm leading-relaxed text-zinc-500">{lp.description}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-20">
        <h3 className="mb-8 flex flex-wrap items-center gap-4 font-mono text-xs uppercase tracking-[0.3em] text-zinc-500">
          <span>
            {"//"} Public GitHub archive —{" "}
            <a
              href={identity.links.github}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="github-profile-link"
              className="text-cyan-400 hover:underline"
            >
              @GameDevKaran
            </a>
          </span>
          {live ? (
            <span data-testid="github-live-badge" className="flex items-center gap-1.5 border border-cyan-400/40 px-2 py-0.5 text-[10px] text-cyan-400">
              <Radio className="h-3 w-3" /> Live sync
            </span>
          ) : (
            <span data-testid="github-snapshot-badge" className="border border-white/15 px-2 py-0.5 text-[10px] text-zinc-600">
              Snapshot · verified Jul 2026
            </span>
          )}
        </h3>
        <div data-testid="github-repo-list" className="grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
          {repos.map((r) => (
            <a
              key={r.name}
              href={r.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`github-repo-${r.name.toLowerCase()}`}
              className="group flex items-center justify-between gap-3 bg-[#050505] p-5 transition-colors duration-300 hover:bg-[#0A0A0A]"
            >
              <div className="flex items-center gap-3 overflow-hidden">
                <Github className="h-4 w-4 shrink-0 text-zinc-600 transition-colors duration-300 group-hover:text-cyan-400" />
                <span className="truncate font-mono text-xs text-zinc-300 group-hover:text-white">{r.name}</span>
              </div>
              <span className="shrink-0 font-mono text-[10px] uppercase text-zinc-600">{r.language}</span>
            </a>
          ))}
        </div>
      </div>
    </Section>
  );
};
